import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message } from "antd";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";
import { removeProcessingDynamicClusteringDay, setProcessingDynamicClusteringDay } from "store/admin-slice/adminServicesSlice";
import { IPickerMappingUpload, IPickerMappingUploadRow } from "common/interface/IPickerUploadScheme";

const UploadDynamicClusteringPage = () => {
  useDocumentTitle("Upload Dynamic Clustering");
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingDynamicClusteringMapping } = useAppSelector((state) => state.adminServices);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);

  useEffect(() => {
    const { timeUploaded } = processingDynamicClusteringMapping;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingDynamicClusteringDay());
      }
    }

    if (processingDynamicClusteringMapping && processingDynamicClusteringMapping.key) {
      navigate(`/${routes.ADMIN_SERVICES.SAVE_DYNAMIC_CLUSTERING}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingDynamicClusteringMapping, navigate, dispatch]);

  useEffect(() => {
    const failed = processingDynamicClusteringMapping?.failedRows?.length;

    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingDynamicClusteringMapping?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IPickerMappingUpload) => {
    dispatch(setProcessingDynamicClusteringDay(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingDynamicClusteringDay());
  };

  const { headerMapping } = processingDynamicClusteringMapping;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingDynamicClusteringMapping?.successRows || [];
  const failedRows = processingDynamicClusteringMapping?.failedRows || [];
  const successData = successRows?.map((row, i) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows?.map((row, i) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];
  const columns: ColumnsType<IPickerMappingUploadRow & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: IPickerMappingUploadRow & { key: string }) => {
        if (key === "errors") {
          const errorMessages = record.errors
            ?.map((error) => error.message)
            .join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (record: IPickerMappingUploadRow & { key: string }) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }

    return "";
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Dynamic Clustering" />
        </Col>
      </Row>

      <Row>
        <Col lg={16} xs={24}>
          <AppUpload
            uploadUrl={apis.uploadDynamicClusteringMapping}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
          />
        </Col>
      </Row>

      {!!Object.keys(processingDynamicClusteringMapping).length && !processingDynamicClusteringMapping.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>

          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={(record) => addBorderToErrorRows(record)}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            ></Table>
          </Row>
        </>
      )}
    </>
  );
};

export default UploadDynamicClusteringPage;
